import { useContext } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { CheckIcon } from "../../../../client/assets/icons/common/CheckIcon";
import { RightArrowIcon } from "../../../../client/assets/icons/common/RightArrowIcon";
import { TrustPilotLogo } from "../../../../client/assets/icons/common/TrustPilotLogo";
import { BaseBenefitsList } from "../../../../components/BaseComponents/BaseBenefitsList";
import { BaseCompanyLogo } from "../../../../components/BaseComponents/BaseCompanyLogo";
import BaseCompanyName from "../../../../components/BaseComponents/BaseCompanyName";
import { BaseFeaturedCompanyHeader } from "../../../../components/BaseComponents/BaseFeaturedCompanyHeader";
import BaseGridRatingHorizontal from "../../../../components/BaseComponents/BaseGridRatingHorizontal";
import BaseLinkWrapper from "../../../../components/BaseComponents/BaseLinkWrapper";
import BaseProductDescription from "../../../../components/BaseComponents/BaseProductDescription";
import BaseServiceFlag from "../../../../components/BaseComponents/BaseServiceFlag";
import { BaseSocialProof } from "../../../../components/BaseComponents/BaseSocialProof";
import { BaseVisitLink } from "../../../../components/BaseComponents/BaseVisitLink";
import { CompanyContext } from "../../../../components/CompanyList/CompanyContext";
import { getAwardText } from "../../../../selectors";
import { ClockIcon } from "../../../assets/ClockIcon";
import { FooterColumns } from "./FooterColumns";
import { getStyles } from "./styles";

const WebAndTrustpilotRatings = ({ styles, trustPilotCount }) => (
	<div className="tw:mt-2 tw:flex tw:w-full tw:flex-row tw:items-center tw:justify-evenly tw:rounded-[12px] tw:bg-[#FAFAFA] tw:px-4 tw:py-3 tw:lg:flex-col tw:lg:py-4">
		<BaseGridRatingHorizontal classNames={styles.rating} />
		<div className="tw:mx-2 tw:my-2 tw:h-[34px] tw:w-px tw:bg-[#E6E6E6] tw:lg:mx-auto tw:lg:h-px tw:lg:w-[102px]" />
		<div className="tw:flex tw:flex-col tw:items-center tw:justify-center">
			<p className="tw:pt-1 tw:text-sm tw:leading-4 tw:font-semibold tw:text-[#212121]">
				{trustPilotCount.toLocaleString()} Reviews
			</p>
			<TrustPilotLogo className="tw:h-4 tw:w-auto" />
		</div>
	</div>
);

export const BusinessLendingCompany = () => {
	const { company, companyIndex, isFeaturedCompany } =
		useContext(CompanyContext);
	const awardText = useSelector((state) => getAwardText(state, { company }));
	const trustPilotCount = company.fields.trustpilotReviewCount;
	const styles = getStyles({ companyIndex });

	return (
		<div
			className={`tw:block tw:rounded-[12px] tw:bg-white tw:shadow-[0px_0px_2px_0px_rgba(24,39,75,0.18),0px_6px_12px_-6px_rgba(24,39,75,0.12),0px_8px_24px_-4px_rgba(24,39,75,0.08)] tw:transition tw:hover:shadow-[0px_8px_32px_0px_rgba(0,0,0,0.08),0px_2px_20px_0px_rgba(0,0,0,0.04)] ${
				isFeaturedCompany ? "tw:ring-2 tw:ring-[#0756C0]" : ""
			}`}
		>
			<BaseFeaturedCompanyHeader {...styles.featuredCompanyHeader} />
			<div className="tw:relative">
				<BaseServiceFlag classNames={styles.serviceFlag} />
				<div className="tw:mx-auto tw:flex tw:max-w-[400px] tw:flex-col tw:gap-4 tw:p-4 tw:md:max-w-[500px] tw:lg:max-w-none tw:lg:flex-row tw:lg:gap-0 tw:lg:p-6">
					<BaseLinkWrapper
						className={`tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:pb-0 tw:lg:w-[204px] tw:lg:py-0 tw:lg:pr-3 tw:xl:w-[216px] ${
							awardText ? "tw:pt-11" : "tw:pt-4"
						}`}
					>
						<div className="tw:flex tw:w-40 tw:flex-col tw:items-center tw:justify-center tw:gap-2">
							<BaseCompanyLogo
								classNames={styles.companyLogo}
								width={160}
								height={160}
							/>
						</div>
					</BaseLinkWrapper>

					{/* Right Hand Column */}
					<BaseLinkWrapper className="tw:flex tw:w-full tw:flex-col">
						<div className="tw:flex tw:flex-col tw:items-stretch tw:lg:flex-row tw:lg:items-center">
							{/* Benefits Column */}
							<div className="tw:flex tw:w-full tw:grow tw:flex-col tw:lg:max-w-none tw:lg:px-3">
								<div className="tw:flex tw:w-full tw:flex-col">
									<BaseCompanyName className="tw:hidden tw:text-2xl tw:leading-8 tw:font-bold tw:tracking-[-0.24px] tw:text-[#212121] tw:lg:block" />
									<BaseProductDescription className="tw:mt-0 tw:text-center tw:text-xl tw:leading-6 tw:font-medium tw:tracking-[-0.2px] tw:text-[#212121] tw:lg:mt-1 tw:lg:max-w-none tw:lg:text-start tw:lg:text-xl tw:lg:leading-[1.75rem]" />

									<div className="tw:block tw:lg:hidden">
										<WebAndTrustpilotRatings
											trustPilotCount={trustPilotCount}
											styles={styles}
										/>
									</div>

									{companyIndex === 0 && (
										<div className="tw:mt-2 tw:flex tw:w-full tw:items-center tw:justify-center tw:rounded-lg tw:bg-[#212121] tw:px-2 tw:py-1.5 tw:lg:w-fit tw:lg:justify-start tw:lg:rounded-[42px]">
											<ClockIcon />
											<p className="tw:pt-[2px] tw:pl-2 tw:text-xs tw:font-medium tw:text-white tw:lg:text-sm">
												Apply Online in Minutes
											</p>
										</div>
									)}

									<div className="tw:hidden tw:lg:block">
										<BaseBenefitsList
											{...styles.benefitsList}
											icon={
												<CheckIcon className="tw:text-xl tw:text-[#0756C0] tw:sm:mt-px" />
											}
										/>
									</div>
								</div>
							</div>

							<div className="tw:hidden tw:lg:block">
								<WebAndTrustpilotRatings
									trustPilotCount={trustPilotCount}
									styles={styles}
								/>
							</div>

							{/* Visit Link Column */}
							<div className="tw:mt-2 tw:flex tw:w-full tw:shrink-0 tw:lg:mt-0 tw:lg:w-[212px] tw:lg:max-w-none tw:lg:pl-3">
								<div className="tw:flex tw:w-full tw:flex-col tw:items-center tw:justify-center tw:gap-4">
									{companyIndex === 0 && (
										<>
											<svg
												style={{ width: 0, height: 0, position: "absolute" }}
												aria-hidden="true"
												focusable="false"
											>
												<linearGradient
													x1="16"
													x2="16"
													y1="0"
													y2="32"
													gradientUnits="userSpaceOnUse"
													id="MortgageRatesCompanyGradient"
												>
													<stop stopColor="#0756C0" />
													<stop offset="1" stopColor="#2F97F4" />
												</linearGradient>
											</svg>
											<div className="tw:hidden tw:lg:block">
												<BaseSocialProof
													contentIconFill="url(#MortgageRatesCompanyGradient)"
													{...styles.socialProof}
												/>
											</div>
										</>
									)}
									<div className="tw:flex tw:w-full tw:max-w-none tw:flex-wrap tw:gap-3 tw:lg:max-w-[260px] tw:lg:gap-4">
										<BaseVisitLink
											{...styles.visitLink}
											icon={<RightArrowIcon />}
										>
											View Rates
										</BaseVisitLink>
									</div>
									{companyIndex === 0 && (
										<div className="tw:block tw:lg:hidden">
											<BaseSocialProof
												contentIconFill="url(#MortgageRatesCompanyGradient)"
												{...styles.socialProof}
											/>
										</div>
									)}
								</div>
							</div>
						</div>
						<div className="tw:mt-6 tw:flex tw:flex-col">
							<FooterColumns />
							<div className="tw:block tw:lg:hidden">
								<BaseBenefitsList
									{...styles.benefitsList}
									icon={
										<CheckIcon className="tw:text-xl tw:text-[#0756C0] tw:sm:mt-px" />
									}
								/>
							</div>
						</div>
					</BaseLinkWrapper>
				</div>
			</div>
		</div>
	);
};

WebAndTrustpilotRatings.propTypes = {
	styles: PropTypes.object,
	trustPilotCount: PropTypes.number,
};
